import {
  inMemoryPersistence,
  setPersistence,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import { auth } from "./config";
import { env } from "../env";
import { getProfile } from "../profile";
import { useAuthStore } from "../stores/auth";

/**
 * Signs this tab in to Firebase AS THE APP USER, so the Firestore rules can
 * scope every read: they allow a doc only when `request.auth.uid` is in its
 * `accessibleUserIds`, which the backend fills with the hashed user id. The
 * token comes from `GET /profile`'s `firebaseToken` (a custom token minted for
 * that same hashed id) — no separate token endpoint.
 *
 * Resolves the signed-in Firebase uid (= the hashed user id, what a query must
 * filter `accessibleUserIds` on), or `null` when there is nothing to sign in as
 * (Firebase not configured, logged out, no token, or sign-in failed). Never
 * rejects.
 *
 * Memoized per app user: concurrent callers share one sign-in. A failed attempt
 * is forgotten so the next caller retries; logout clears it
 * (`teardownFirebaseAuth`). Persistence is in-memory so the session is per tab,
 * like the app's own (sessionStorage) auth — with the default shared
 * persistence, signing in as another user in a second tab would swap this
 * tab's Firebase user and every listener here would start being denied.
 *
 * Logout / user-change safety: a sign-in can still be in flight when the user
 * logs out (and a new user logs in). Two guards keep a late sign-in from
 * landing as the wrong user:
 * - `generation` is bumped on every logout or user change; a sign-in started
 *   under an older generation abandons itself instead of signing in.
 * - Every Firebase auth operation (sign-in, sign-out) runs through `authQueue`,
 *   strictly in order, so a sign-in that already started finishes BEFORE the
 *   logout's `signOut` runs, and the next user's sign-in runs after it.
 */
let ready: { userId: number; promise: Promise<string | null> } | undefined;
let generation = 0;
let authQueue: Promise<unknown> = Promise.resolve();

function isFirebaseConfigured(): boolean {
  return Boolean(env.firebaseProjectId && env.firebaseApiKey);
}

/** Runs `op` after every previously queued auth operation has settled. */
function enqueueAuthOp<T>(op: () => Promise<T>): Promise<T> {
  const run = authQueue.then(op, op);
  authQueue = run.catch(() => undefined);
  return run;
}

export function ensureFirebaseAuthReady(): Promise<string | null> {
  if (!isFirebaseConfigured()) return Promise.resolve(null);
  // Logged out: nothing to sign in as, and `/profile` would 401. Not memoized,
  // so a call after login signs in.
  const userId = useAuthStore.getState().user?.id;
  if (userId == null) return Promise.resolve(null);

  if (!ready || ready.userId !== userId) {
    // A different user than the one memoized: that user's in-flight sign-in
    // (if any) must not complete over this one.
    if (ready) generation += 1;
    const promise = signIn(generation).then((uid) => {
      if (uid == null && ready?.promise === promise) ready = undefined;
      return uid;
    });
    ready = { userId, promise };
  }
  return ready.promise;
}

async function signIn(gen: number): Promise<string | null> {
  try {
    const { firebaseToken } = await getProfile();
    // Logged out (or another user took over) while the profile was loading.
    if (gen !== generation) return null;
    if (!firebaseToken) {
      console.warn("[firebase] no Firebase token on the profile; live updates are off");
      return null;
    }
    return await enqueueAuthOp(async () => {
      // Re-checked inside the queue: a logout queued ahead of us has run by now.
      if (gen !== generation) return null;
      await setPersistence(auth, inMemoryPersistence);
      const credential = await signInWithCustomToken(auth, firebaseToken);
      return credential.user.uid;
    });
  } catch (error: unknown) {
    console.error("[firebase] sign-in failed:", error);
    return null;
  }
}

/**
 * Logout teardown (`clearTransientStores`): forget the memoized sign-in and sign
 * the tab out of Firebase, so the next account on this tab never reads with the
 * previous user's Firebase identity. The sign-out is queued behind any sign-in
 * already running, so it always has the last word over that sign-in.
 */
export function teardownFirebaseAuth(): void {
  generation += 1;
  ready = undefined;
  if (!isFirebaseConfigured()) return;
  void enqueueAuthOp(() => signOut(auth)).catch((error: unknown) => {
    console.error("[firebase] sign-out failed:", error);
  });
}
